(() => {
  const TAU = Math.PI * 2;
  const FIBERS = 19;
  const SAMPLES = 112;
  const CORE_FIBER = Math.floor(FIBERS / 2);
  const SPHERE_RADIUS = 0.78;
  const DUBAI_LATITUDE = 25.2048 * Math.PI / 180;
  const DUBAI_LONGITUDE = 55.2708 * Math.PI / 180;
  const DUBAI_POINT = (() => {
    const latitudeRadius = Math.cos(DUBAI_LATITUDE) * SPHERE_RADIUS;
    return new Float32Array([
      latitudeRadius * Math.cos(DUBAI_LONGITUDE),
      -Math.sin(DUBAI_LATITUDE) * SPHERE_RADIUS,
      latitudeRadius * Math.sin(DUBAI_LONGITUDE)
    ]);
  })();

  const MOBILE_LAYOUTS = [
    { x: 0.60, y: 0.29, width: 0.82, rx: -0.12, ry: 0.1, rz: -0.04 },
    { x: 0.27, y: 0.3, width: 0.8, rx: 0.36, ry: -0.22, rz: -0.22 },
    { x: 0.73, y: 0.29, width: 0.94, rx: -0.08, ry: 0.06, rz: -0.02 },
    { x: 0.29, y: 0.3, width: 0.86, rx: 0.08, ry: -0.08, rz: 0.05 },
    { x: 0.5, y: 0.34, width: 1.12, rx: 0, ry: 0, rz: 0 }
  ];

  const TABLET_LAYOUTS = [
    { x: 0.7, y: 0.31, width: 0.65, rx: -0.1, ry: 0.1, rz: -0.04 },
    { x: 0.3, y: 0.32, width: 0.63, rx: 0.36, ry: -0.22, rz: -0.22 },
    { x: 0.7, y: 0.31, width: 0.74, rx: -0.06, ry: 0.05, rz: -0.02 },
    { x: 0.31, y: 0.32, width: 0.69, rx: 0.07, ry: -0.07, rz: 0.05 },
    { x: 0.5, y: 0.35, width: 1.04, rx: 0, ry: 0, rz: 0 }
  ];

  const DESKTOP_LAYOUTS = [
    { x: 0.73, y: 0.5, width: 0.48, rx: -0.08, ry: 0.09, rz: -0.035 },
    { x: 0.27, y: 0.5, width: 0.46, rx: 0.36, ry: -0.22, rz: -0.22 },
    { x: 0.73, y: 0.5, width: 0.56, rx: -0.05, ry: 0.05, rz: -0.02 },
    { x: 0.28, y: 0.5, width: 0.5, rx: 0.06, ry: -0.06, rz: 0.045 },
    { x: 0.5, y: 0.5, width: 0.94, rx: 0, ry: 0, rz: 0 }
  ];

  const clamp = (value, min = 0, max = 1) => Math.max(min, Math.min(max, value));
  const lerp = (from, to, amount) => from + (to - from) * amount;
  const smootherstep = (value) => {
    const t = clamp(value);
    return t * t * t * (t * (t * 6 - 15) + 10);
  };

  const buildSignalStates = () => {
    const states = Array.from({ length: 5 }, () => new Float32Array(FIBERS * SAMPLES * 3));

    for (let fiber = 0; fiber < FIBERS; fiber += 1) {
      const f = (fiber - CORE_FIBER) / CORE_FIBER;
      const phase = (fiber / FIBERS) * TAU;

      for (let sample = 0; sample < SAMPLES; sample += 1) {
        const t = sample / (SAMPLES - 1);
        const u = t * TAU;
        const index = (fiber * SAMPLES + sample) * 3;

        const closure = TAU * 0.15 * smootherstep((t - 0.75) / 0.25);
        const twist = phase + u * 2.15 - closure;
        const mindRadius = 0.7 + Math.cos(twist) * 0.2;
        states[0][index] = Math.cos(u) * mindRadius;
        states[0][index + 1] = Math.sin(u) * mindRadius;
        states[0][index + 2] = Math.sin(twist) * 0.24 + f * 0.018;

        if (fiber < 12) {
          const longitude = fiber === CORE_FIBER ? DUBAI_LONGITUDE : (fiber / 12) * Math.PI + 0.17;
          states[1][index] = Math.cos(u) * Math.cos(longitude) * SPHERE_RADIUS;
          states[1][index + 1] = Math.sin(u) * SPHERE_RADIUS;
          states[1][index + 2] = Math.cos(u) * Math.sin(longitude) * SPHERE_RADIUS;
        } else {
          const latitude = [-66, -43, -19, 8, 32, 53, 71][fiber - 12] * Math.PI / 180;
          const radius = Math.cos(latitude) * SPHERE_RADIUS;
          states[1][index] = Math.cos(u) * radius;
          states[1][index + 1] = -Math.sin(latitude) * SPHERE_RADIUS;
          states[1][index + 2] = Math.sin(u) * radius;
        }

        const tideX = (t - 0.5) * 2.45;
        states[2][index] = tideX;
        states[2][index + 1] = Math.sin(tideX * 2.1 + phase * 0.22) * 0.28 + f * 0.22;
        states[2][index + 2] = Math.cos(tideX * 1.45 - phase * 0.18) * 0.18 + f * 0.06;

        const ensoU = -0.12 + t * TAU * 0.91;
        const ensoRadius = 0.72 + Math.sin(ensoU * 3 + phase * 0.1) * 0.035 + f * 0.075;
        states[3][index] = Math.cos(ensoU) * ensoRadius;
        states[3][index + 1] = Math.sin(ensoU) * ensoRadius;
        states[3][index + 2] = f * 0.14 + Math.sin(ensoU * 2) * 0.05;

        states[4][index] = (t - 0.5) * 3.2;
        const taper = Math.pow(Math.sin(Math.PI * t), 1.4);
        states[4][index + 1] = taper * (f * 0.025 + 0.026 * Math.sin(u - 0.3));
        states[4][index + 2] = taper * f * 0.016;
      }
    }

    return states;
  };

  let signalStates;

  class EnsoSignal extends HTMLElement {
    connectedCallback() {
      if (this.cleanupSignal) return;
      try {
        this.cleanupSignal = this.initialize();
      } catch {
        this.cleanupSignal?.();
        this.cleanupSignal = null;
        this.showFallback();
      }
    }

    disconnectedCallback() {
      if (this.cleanupSignal) this.cleanupSignal();
      this.cleanupSignal = null;
    }

    showFallback() {
      const hero = this.closest("[data-enso-hero]");
      if (hero) {
        delete hero.dataset.signalEnhanced;
        hero.querySelectorAll("[data-hero-copy]").forEach((node) => node.removeAttribute("aria-hidden"));
      }
      const root = document.documentElement;
      root.dataset.canvas = "fallback";
      root.dataset.signalReady = "true";
      delete root.dataset.signalActive;
      delete root.dataset.signalLoader;
    }

    initialize() {
      const canvas = this.querySelector(".enso-signal-canvas");
      const hero = this.closest("[data-enso-hero]");
      const stage = hero?.querySelector(".enso-hero-stage");
      if (!canvas || !hero || !stage) return () => {};

      const root = document.documentElement;
      const context = canvas.getContext("2d", { alpha: true });
      const motionPreference = window.matchMedia("(prefers-reduced-motion: reduce)");
      let reducedMotion = motionPreference.matches;
      const colorScheme = window.matchMedia("(prefers-color-scheme: light)");
      const designMode = document.body.classList.contains("enso-design-mode");
      let repeatVisit = designMode;
      let loaderTimer = 0;
      let unlockTimer = 0;
      let canvasReady = false;
      let disposed = false;

      if (!designMode) {
        try {
          repeatVisit = window.sessionStorage.getItem("enso-landing-v2-seen:v1") === "1";
        } catch {
          repeatVisit = false;
        }
      }

      root.dataset.signalVisit = repeatVisit ? "repeat" : "first";
      if (!designMode) root.dataset.signalActive = "true";
      root.dataset.canvas = context ? "initializing" : "fallback";
      root.dataset.signalLoader = "pending";
      delete root.dataset.signalReady;

      const reveal = () => {
        if (disposed) return;
        canvasReady = true;
        window.clearTimeout(loaderTimer);
        root.dataset.signalReady = "true";
        root.dataset.signalLoader = root.dataset.signalLoader === "visible" ? "resolved" : "hidden";
        try {
          window.sessionStorage.setItem("enso-landing-v2-seen:v1", "1");
        } catch {}
        const handoff = reducedMotion || designMode ? 0 : repeatVisit ? 180 : 280;
        unlockTimer = window.setTimeout(() => {
          if (!disposed) delete root.dataset.signalActive;
        }, handoff);
      };

      const scheduleReveal = () => {
        reveal();
      };

      if (!reducedMotion && !designMode) {
        loaderTimer = window.setTimeout(() => {
          if (!disposed && !canvasReady) root.dataset.signalLoader = "visible";
        }, 180);
      }

      if (!context) {
        window.clearTimeout(loaderTimer);
        this.showFallback();
        return () => {
          disposed = true;
          window.clearTimeout(loaderTimer);
          window.clearTimeout(unlockTimer);
          delete root.dataset.signalActive;
          delete root.dataset.signalLoader;
        };
      }

      const SIGNAL_STATES = signalStates || (signalStates = buildSignalStates());
      hero.dataset.signalEnhanced = "true";
      const projected = new Float32Array(FIBERS * SAMPLES * 2);
      const projectedDepth = new Float32Array(FIBERS * SAMPLES);
      const copyNodes = Array.from(hero.querySelectorAll("[data-hero-copy]"));
      const progressNodes = Array.from(hero.querySelectorAll("[data-hero-progress]"));
      const chapterNodes = Array.from(hero.querySelectorAll("[data-hero-chapter]"));
      const marker = hero.querySelector("[data-dubai-marker]");
      const markerLabel = marker?.querySelector("span");
      const dubaiProjected = new Float32Array(2);
      const resolution = hero.querySelector("[data-resolution]");
      const progressHandlers = [];
      let width = 1;
      let height = 1;
      let dpr = 1;
      let heroTop = 0;
      let scrollEnd = 1;
      let chapterOffsets = [0, 1, 2, 3];
      let frame = 0;
      let targetProgress = 0;
      let visualProgress = 0;
      let lastTime = performance.now();
      let pageVisible = !document.hidden;
      let heroVisible = true;
      let geometryDirty = true;
      let geometryPosition = NaN;
      let uiPosition = NaN;
      let fiberPaths = [];
      let depthPaths = [];
      let layouts = MOBILE_LAYOUTS;
      const projection = {
        centerX: 0,
        centerY: 0,
        scale: 1,
        cosX: 1,
        sinX: 0,
        cosY: 1,
        sinY: 0,
        cosZ: 1,
        sinZ: 0
      };

      const configureProjection = (layout) => {
        projection.centerX = layout.x * width;
        projection.centerY = layout.y * height;
        projection.scale = Math.min(width, height * 1.08) * layout.width * 0.48;
        projection.cosX = Math.cos(layout.rx);
        projection.sinX = Math.sin(layout.rx);
        projection.cosY = Math.cos(layout.ry);
        projection.sinY = Math.sin(layout.ry);
        projection.cosZ = Math.cos(layout.rz);
        projection.sinZ = Math.sin(layout.rz);
      };

      const projectPoint = (x, y, z, target, index) => {
        const xY = x * projection.cosY + z * projection.sinY;
        const zY = -x * projection.sinY + z * projection.cosY;
        const yX = y * projection.cosX - zY * projection.sinX;
        const zX = y * projection.sinX + zY * projection.cosX;
        const xZ = xY * projection.cosZ - yX * projection.sinZ;
        const yZ = xY * projection.sinZ + yX * projection.cosZ;
        const perspective = 1 / (1 + zX * 0.22);
        target[index] = projection.centerX + xZ * projection.scale * perspective;
        target[index + 1] = projection.centerY + yZ * projection.scale * perspective;
        return zX;
      };

      const readPalette = () => {
        const style = getComputedStyle(root);
        return {
          background: style.getPropertyValue("--background").trim() || "#030406",
          silver: style.getPropertyValue("--silver-canvas").trim() || "#aeb7c2",
          silverSoft: style.getPropertyValue("--silver-soft-canvas").trim() || "#77818d",
          accent: style.getPropertyValue("--accent").trim() || "#df4557",
          accentHot: style.getPropertyValue("--accent-hot").trim() || "#fff4f6"
        };
      };

      let palette = readPalette();

      const measure = () => {
        const stageRect = stage.getBoundingClientRect();
        const nextWidth = Math.max(1, Math.round(stageRect.width));
        const nextHeight = Math.max(1, Math.round(stageRect.height));
        const nextLayouts = nextWidth >= 1200 || (nextWidth >= 900 && nextWidth / nextHeight >= 1.15)
          ? DESKTOP_LAYOUTS
          : nextWidth >= 768
            ? TABLET_LAYOUTS
            : MOBILE_LAYOUTS;
        const cap = nextWidth < 768 ? 2.25 : nextWidth < 1200 ? 1.75 : 1.5;
        const budget = nextWidth < 768 ? 2200000 : nextWidth < 1200 ? 4200000 : 5000000;
        const nextDpr = Math.max(1, Math.min(
          window.devicePixelRatio || 1,
          cap,
          Math.sqrt(budget / Math.max(1, nextWidth * nextHeight))
        ));
        const geometryChanged = nextWidth !== width || nextHeight !== height || Math.abs(nextDpr - dpr) > 0.001;

        heroTop = window.scrollY + hero.getBoundingClientRect().top;
        chapterOffsets = chapterNodes.map((node) => node.offsetTop);
        scrollEnd = Math.max(chapterOffsets[3] + 1, hero.offsetHeight - nextHeight);

        if (!geometryChanged) return false;

        geometryDirty = true;
        uiPosition = NaN;
        width = nextWidth;
        height = nextHeight;
        dpr = nextDpr;
        layouts = nextLayouts;
        canvas.width = Math.round(width * dpr);
        canvas.height = Math.round(height * dpr);
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
        context.setTransform(dpr, 0, 0, dpr, 0, 0);

        if (marker) {
          configureProjection(layouts[1]);
          projectPoint(DUBAI_POINT[0], DUBAI_POINT[1], DUBAI_POINT[2], dubaiProjected, 0);
          const labelWidth = markerLabel?.getBoundingClientRect().width || 0;
          const edgeGutter = width < 768 ? 18 : width < 1200 ? width * 0.06 : width * 0.065;
          let sphereLeft = width;
          let sphereRight = 0;
          for (let fiber = 0; fiber < FIBERS; fiber += 1) {
            for (let sample = 0; sample < SAMPLES; sample += 1) {
              const sourceIndex = (fiber * SAMPLES + sample) * 3;
              const targetIndex = (fiber * SAMPLES + sample) * 2;
              projectPoint(
                SIGNAL_STATES[1][sourceIndex],
                SIGNAL_STATES[1][sourceIndex + 1],
                SIGNAL_STATES[1][sourceIndex + 2],
                projected,
                targetIndex
              );
              sphereLeft = Math.min(sphereLeft, projected[targetIndex]);
              sphereRight = Math.max(sphereRight, projected[targetIndex]);
            }
          }

          const rightLabelX = clamp(
            Math.max(dubaiProjected[0] + 34, sphereRight + (width < 768 ? 18 : 24)),
            edgeGutter,
            width - edgeGutter - labelWidth
          );
          const leftLabelX = clamp(
            Math.min(dubaiProjected[0] - labelWidth - 34, sphereLeft - labelWidth - 24),
            edgeGutter,
            width - edgeGutter - labelWidth
          );
          const rightOffset = rightLabelX - dubaiProjected[0];
          const leftOffset = dubaiProjected[0] - (leftLabelX + labelWidth);
          const side = width < 768 || rightOffset >= 18 || leftOffset < rightOffset ? "right" : "left";
          const labelOffset = Math.max(18, side === "right" ? rightOffset : leftOffset);
          marker.dataset.markerSide = side;
          marker.style.setProperty("--marker-label-offset", `${labelOffset}px`);
        }

        return true;
      };

      const updateTarget = () => {
        const localScroll = clamp(window.scrollY - heroTop, chapterOffsets[0], scrollEnd);
        let scenePosition = 4;
        for (let index = 0; index < 4; index += 1) {
          const start = chapterOffsets[index];
          const end = index < 3 ? chapterOffsets[index + 1] : scrollEnd;
          if (localScroll <= end) {
            scenePosition = index + clamp((localScroll - start) / Math.max(1, end - start));
            break;
          }
        }
        targetProgress = scenePosition / 4;
      };

      const interpolateLayout = (position) => {
        const segment = Math.min(3, Math.floor(position));
        const amount = smootherstep(position - segment);
        const from = layouts[segment];
        const to = layouts[segment + 1];
        return {
          x: lerp(from.x, to.x, amount),
          y: lerp(from.y, to.y, amount),
          width: lerp(from.width, to.width, amount),
          rx: lerp(from.rx, to.rx, amount),
          ry: lerp(from.ry, to.ry, amount),
          rz: lerp(from.rz, to.rz, amount)
        };
      };

      const paint = (now) => {
        const position = clamp(visualProgress * 4, 0, 4);
        const segment = Math.min(3, Math.floor(position));
        const amount = smootherstep(position - segment);
        const layout = interpolateLayout(position);
        const fromState = SIGNAL_STATES[segment];
        const toState = SIGNAL_STATES[segment + 1];
        configureProjection(layout);

        context.clearRect(0, 0, width, height);

        if (geometryDirty || geometryPosition !== position) {
        for (let fiber = 0; fiber < FIBERS; fiber += 1) {
          for (let sample = 0; sample < SAMPLES; sample += 1) {
            const sourceIndex = (fiber * SAMPLES + sample) * 3;
            const targetIndex = (fiber * SAMPLES + sample) * 2;
            const x = lerp(fromState[sourceIndex], toState[sourceIndex], amount);
            const y = lerp(fromState[sourceIndex + 1], toState[sourceIndex + 1], amount);
            const z = lerp(fromState[sourceIndex + 2], toState[sourceIndex + 2], amount);
            projectedDepth[fiber * SAMPLES + sample] = projectPoint(x, y, z, projected, targetIndex);
          }
        }

          fiberPaths = Array.from({ length: FIBERS }, (_, fiber) => {
            const path = new Path2D();
            for (let sample = 0; sample < SAMPLES; sample += 1) {
              const index = (fiber * SAMPLES + sample) * 2;
              if (sample === 0) path.moveTo(projected[index], projected[index + 1]);
              else path.lineTo(projected[index], projected[index + 1]);
            }
            return path;
          });
          depthPaths = Array.from({ length: 16 }, () => new Path2D());
          for (let fiber = 0; fiber < FIBERS; fiber += 1) {
            let previousBucket = -1;
            for (let sample = 1; sample < SAMPLES; sample += 1) {
              const point = fiber * SAMPLES + sample;
              const depth = (projectedDepth[point] + projectedDepth[point - 1]) * 0.5;
              const bucket = Math.min(15, Math.floor(clamp((depth + 0.8) / 1.6) * 16));
              const path = depthPaths[bucket];
              if (bucket !== previousBucket) path.moveTo(projected[(point - 1) * 2], projected[(point - 1) * 2 + 1]);
              previousBucket = bucket;
              path.lineTo(projected[point * 2], projected[point * 2 + 1]);
            }
          }
          geometryDirty = false;
          geometryPosition = position;
        }

        const dubaiPresence = 1 - smootherstep((Math.abs(position - 1) - 0.22) / 0.12);

        const drawFiber = (fiber, stroke, lineWidth) => {
          context.strokeStyle = stroke;
          context.lineWidth = lineWidth;
          context.lineCap = "round";
          context.lineJoin = "round";
          context.stroke(fiberPaths[fiber]);
        };

        const drawPulse = (opacity = 1) => {
          if (opacity <= 0) return;
          const pulsePosition = (((reducedMotion ? 0 : now) / 42000 + 0.16) % 1) * Math.max(1, SAMPLES - 2);
          const pulseSample = Math.floor(pulsePosition);
          const pulseAmount = pulsePosition - pulseSample;
          const pulseIndexA = (CORE_FIBER * SAMPLES + pulseSample) * 2;
          const pulseIndexB = pulseIndexA + 2;
          const pulseX = lerp(projected[pulseIndexA], projected[pulseIndexB], pulseAmount);
          const pulseY = lerp(projected[pulseIndexA + 1], projected[pulseIndexB + 1], pulseAmount);
          if (opacity < 1) context.globalAlpha = opacity;
          context.beginPath();
          context.arc(pulseX, pulseY, 2.2, 0, TAU);
          context.fillStyle = palette.silver;
          context.fill();
          if (opacity < 1) context.globalAlpha = 1;
        };

        const depthPresence = 1 - smootherstep((position - 1.25) / 0.65);
        const horizonPresence = smootherstep((position - 3.32) / 0.68);
        if (depthPresence < 1) {
          for (let fiber = 0; fiber < FIBERS; fiber += 1) {
            const distance = Math.abs(fiber - CORE_FIBER) / CORE_FIBER;
            const isCore = fiber === CORE_FIBER;
            context.globalAlpha = (1 - depthPresence) * (1 - horizonPresence * 0.7);
            drawFiber(fiber, palette.background, isCore ? 4.4 : 3.1);
            context.globalAlpha = (1 - depthPresence) * (1 - horizonPresence * distance * 0.62);
            drawFiber(
              fiber,
              distance > 0.64 ? palette.silverSoft : palette.silver,
              (isCore ? 1.55 : 1.05) * (1 - horizonPresence * 0.35)
            );
          }
        }
        if (depthPresence > 0) {
          context.lineCap = "round";
          context.lineJoin = "round";
          context.strokeStyle = palette.silver;
          for (let bucket = 15; bucket >= 0; bucket -= 1) {
            const light = 1 - bucket / 15;
            context.globalAlpha = depthPresence * (0.12 + 0.8 * light * light);
            context.lineWidth = 0.7 + light * 0.45;
            context.stroke(depthPaths[bucket]);
          }
        }
        context.globalAlpha = 1;
        drawPulse(1 - dubaiPresence);

        if (uiPosition === position) return;
        uiPosition = position;

        copyNodes.forEach((node, index) => {
          const distance = Math.abs(position - index);
          const opacity = 1 - smootherstep((distance - 0.1) / 0.52);
          node.style.setProperty("--copy-opacity", String(opacity));
          node.style.setProperty("--copy-shift", `${(1 - opacity) * 11}px`);
          const hidden = opacity < 0.42 ? "true" : "false";
          if (node.getAttribute("aria-hidden") !== hidden) node.setAttribute("aria-hidden", hidden);
        });

        progressNodes.forEach((node, index) => {
          const fill = clamp(position - index);
          node.style.setProperty("--step-progress", String(fill));
          const active = String(Math.round(clamp(position, 0, 3)) === index);
          if (node.dataset.active !== active) node.dataset.active = active;
          if (active === "true" && node.getAttribute("aria-current") !== "step") node.setAttribute("aria-current", "step");
          else if (active !== "true" && node.hasAttribute("aria-current")) node.removeAttribute("aria-current");
        });

        if (marker) {
          const revealStage = (enterAt, leaveAt) => (
            smootherstep((position - enterAt) / 0.05)
            * (1 - smootherstep((position - leaveAt) / 0.05))
          );
          const coreReveal = revealStage(0.82, 1.34);
          const leaderReveal = revealStage(0.87, 1.29);
          const labelReveal = revealStage(0.91, 1.25);
          const coordinateReveal = revealStage(0.95, 1.21);
          projectPoint(DUBAI_POINT[0], DUBAI_POINT[1], DUBAI_POINT[2], dubaiProjected, 0);
          marker.style.setProperty("--marker-opacity", String(coreReveal));
          marker.style.setProperty("--marker-core-opacity", String(coreReveal));
          marker.style.setProperty("--marker-core-scale", String(0.76 + coreReveal * 0.24));
          marker.style.setProperty("--marker-leader-progress", String(leaderReveal));
          marker.style.setProperty("--marker-label-opacity", String(labelReveal));
          marker.style.setProperty("--marker-coordinate-opacity", String(coordinateReveal));
          marker.style.setProperty("--marker-label-shift", `${(1 - labelReveal) * 6}px`);
          marker.style.setProperty("--marker-x", `${dubaiProjected[0]}px`);
          marker.style.setProperty("--marker-y", `${dubaiProjected[1]}px`);
        }

        if (resolution) {
          resolution.style.setProperty("--resolution-opacity", String(smootherstep((position - 3.72) / 0.22)));
        }
      };

      const requestPaint = () => {
        if (!disposed && pageVisible && heroVisible && !frame) frame = window.requestAnimationFrame(animate);
      };

      const animate = (now) => {
        frame = 0;
        if (disposed || !pageVisible || !heroVisible) return;
        const delta = Math.min(0.05, (now - lastTime) / 1000);
        lastTime = now;
        if (reducedMotion) visualProgress = targetProgress;
        else {
          const difference = targetProgress - visualProgress;
          if (Math.abs(difference) < 0.00005) visualProgress = targetProgress;
          else {
            const damping = 2.65 + Math.min(Math.abs(difference) * 8, 2.5);
            visualProgress += difference * (1 - Math.exp(-damping * delta));
          }
        }
        try {
          paint(now);
        } catch {
          this.cleanupSignal?.();
          this.showFallback();
          return;
        }
        if (!reducedMotion) requestPaint();
      };

      const updateVisibility = () => {
        const rect = hero.getBoundingClientRect();
        const wasVisible = heroVisible;
        heroVisible = rect.bottom > 0 && rect.top < window.innerHeight;
        if (!heroVisible || !pageVisible) {
          window.cancelAnimationFrame(frame);
          frame = 0;
        } else {
          if (!wasVisible) {
            measure();
            updateTarget();
            visualProgress = targetProgress;
            lastTime = performance.now();
          }
          requestPaint();
        }
      };

      const onScroll = () => {
        updateTarget();
        updateVisibility();
      };

      const onResize = () => {
        measure();
        updateTarget();
        updateVisibility();
        requestPaint();
      };

      const onScheme = () => {
        palette = readPalette();
        requestPaint();
      };

      const onMotion = () => {
        reducedMotion = motionPreference.matches;
        measure();
        updateTarget();
        visualProgress = targetProgress;
        window.cancelAnimationFrame(frame);
        frame = 0;
        lastTime = performance.now();
        requestPaint();
      };

      const onVisibility = () => {
        pageVisible = !document.hidden;
        if (pageVisible) {
          measure();
          updateTarget();
          visualProgress = targetProgress;
          lastTime = performance.now();
        }
        updateVisibility();
      };

      progressNodes.forEach((node, index) => {
        const handler = () => window.scrollTo({ top: heroTop + chapterOffsets[index], behavior: reducedMotion ? "auto" : "smooth" });
        node.addEventListener("click", handler);
        progressHandlers.push([node, handler]);
      });

      const cleanup = () => {
        disposed = true;
        window.cancelAnimationFrame(frame);
        window.clearTimeout(loaderTimer);
        window.clearTimeout(unlockTimer);
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", onResize);
        colorScheme.removeEventListener("change", onScheme);
        motionPreference.removeEventListener("change", onMotion);
        document.removeEventListener("visibilitychange", onVisibility);
        progressHandlers.forEach(([node, handler]) => node.removeEventListener("click", handler));
        delete root.dataset.signalActive;
        delete root.dataset.signalLoader;
        delete hero.dataset.signalEnhanced;
      };
      this.cleanupSignal = cleanup;

      measure();
      updateTarget();
      visualProgress = targetProgress;
      paint(performance.now());
      root.dataset.canvas = "ready";
      scheduleReveal();

      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onResize, { passive: true });
      colorScheme.addEventListener("change", onScheme);
      motionPreference.addEventListener("change", onMotion);
      document.addEventListener("visibilitychange", onVisibility);
      updateVisibility();

      return cleanup;
    }
  }

  if (!customElements.get("enso-signal")) customElements.define("enso-signal", EnsoSignal);

})();
